import React from 'react';

/* Text tabs, 2px --accent underline on active, --text-secondary inactive. No
   pills, no boxes. TABS NEVER CARRY COUNTS except Attention. The URL carries the
   tab so a deep link from an alert lands exactly where intended.

   ## The underline slides

   Machined register, 2026-08-01. The underline used to be a `border-bottom` on each
   button, so changing tab was one border switching off and another switching on — two
   events the eye reads as a repaint rather than as one movement. Now there is a single
   bar and it travels.

   Three details make it worth the twenty lines:

   - **`transform`, not `left`/`width`.** The bar is 1px wide at the origin and is
     placed with `translateX(...) scaleX(...)`, so travel and resize both run on the
     compositor. Animating `width` would put a layout pass on the main thread every
     frame, and the main thread in this app is drawing charts.
   - **Weight no longer changes with state.** The active tab used to go 400 → 500,
     which reflows the row and nudges every tab after it by a pixel. Colour carries the
     state now; the metrics hold still.
   - **The first paint does not animate.** The transition is gated on until after the
     first frame, so a tab bar arriving on screen has its bar already in place instead
     of sliding in from the left edge. */

export function TabBar({ tabs = [], active, onSelect, style }) {
  const listRef = React.useRef(null);
  const [bar, setBar] = React.useState(null);
  const [ready, setReady] = React.useState(false);

  const measure = React.useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector('[data-on]');
    const next = el ? { x: el.offsetLeft, w: el.offsetWidth } : null;
    // Bail on an unchanged measurement. ResizeObserver fires once per frame while the
    // window is being dragged, and without this each of those frames sets a new object
    // and costs a React render — on a screen that is also redrawing a chart.
    setBar((prev) => (prev && next && prev.x === next.x && prev.w === next.w ? prev : next));
  }, []);

  React.useLayoutEffect(() => {
    measure();
    // Not a one-shot: the container resizes with the window, and the webfont swapping
    // in changes every label's width after the first paint.
    const ro = new ResizeObserver(measure);
    if (listRef.current) ro.observe(listRef.current);
    let cancelled = false;
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => { if (!cancelled) measure(); });
    }
    return () => { cancelled = true; ro.disconnect(); };
  }, [measure, tabs, active]);

  React.useEffect(() => {
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div role="tablist" ref={listRef} style={{
      position: 'relative', display: 'flex', gap: 'var(--space-20)',
      borderBottom: '1px solid var(--border-subtle)', ...style,
    }}>
      {tabs.map((t) => {
        const tab = typeof t === 'string' ? { id: t, label: t } : t;
        const on = active === tab.id;
        return (
          <button key={tab.id} role="tab" aria-selected={on} type="button" data-on={on ? '' : undefined}
            onClick={() => onSelect && onSelect(tab.id)}
            className="jmt-tab" style={{
              background: 'none', border: 'none', padding: '0 0 var(--space-8)', cursor: 'pointer',
              color: on ? 'var(--text-primary)' : 'var(--text-secondary)',
              fontFamily: 'var(--font-ui)', fontSize: 'var(--body-sm-size)', fontWeight: 500,
              letterSpacing: 'var(--body-sm-tracking)', whiteSpace: 'nowrap',
              transition: 'color var(--dur-state) var(--ease-out)',
            }}>
            {tab.label}{tab.count != null ? ' (' + tab.count + ')' : ''}
          </button>
        );
      })}
      <span aria-hidden="true" className="jmt-tab-bar" style={{
        position: 'absolute', left: 0, bottom: -1, width: 1, height: 2,
        background: 'var(--accent)', transformOrigin: 'left',
        transform: bar ? `translateX(${bar.x}px) scaleX(${bar.w})` : 'scaleX(0)',
        opacity: bar ? 1 : 0,
        transition: ready
          ? 'transform var(--dur-menu) var(--ease-in-out), opacity var(--dur-state) var(--ease-out)'
          : 'none',
      }} />
      <style>{`
        .jmt-tab:hover{color:var(--text-primary)}
        .jmt-tab:focus-visible{outline:none;box-shadow:var(--focus-ring)}
        @media (prefers-reduced-motion: reduce){.jmt-tab-bar{transition:none}}
      `}</style>
    </div>
  );
}
